import axios from 'axios'
import Logger from './Logger';

const logger = new Logger('utils');

/**
 * Create a function which will call the callback function
 * after the given amount of milliseconds has passed since
 * the last time the callback function was called.
 */
export const idle = (callback, delay) =>
{
	let handle;

	return () =>
	{
		if (handle)
		{
			clearTimeout(handle);
		}

		handle = setTimeout(callback, delay);
	};
};

/**
 * Error produced when a socket request has a timeout.
 */
export class SocketTimeoutError extends Error
{
	constructor(message)
	{
		super(message);

		this.name = 'SocketTimeoutError';

		if (Error.hasOwnProperty('captureStackTrace')) // Just in V8.
			Error.captureStackTrace(this, SocketTimeoutError);
		else
			this.stack = (new Error(message)).stack;
	}
}

/**
 * 防反跳。fn函数在最后一次调用时刻的delay毫秒之后执行！
 * @param fn 执行函数
 * @param delay 时间间隔
 * @param isImmediate 为true，debounce会在delay时间间隔的开始时立即调用这个函数
 * @returns {Function}
 */
export const debounce = (fn, delay, isImmediate) => {

	let timer = null;  //初始化timer，作为计时清除依据

	return (...args) => {//取得传入参数

		clearTimeout(timer);
		if (isImmediate && timer === null) {
			//时间间隔外立即执行
			fn.apply(this, args);
			timer = 0;
			return;
		}
		timer = setTimeout(() => {
			fn.apply(this, args);
			timer = null;
		}, delay);
	}
}

/**
 * 创建并返回一个像节流阀一样的函数，当重复调用函数的时候，最多每隔delay毫秒调用一次该函数
 * @param fn 执行函数
 * @param delay 时间间隔
 * @returns {Function}
 */
export const throttle = (fn, delay) => {

	let timer = null;

	return (...args) => { // 取得传入参数

		if (!timer) {
			timer = setTimeout(() => {

				fn.apply(this, args);
				timer = null;

			}, delay);
		}
	}
}


let timer = null;
/**
 * 节流打印日志
 * @param {*} message
 * @param {*} delay
 */
export const throttleLogger = (message, delay) => {

	if (!timer) {
		timer = setTimeout(() => {

			logger.debug(message);
			timer = null;

		}, delay);
	}
}

export const wrapperAxios = axios.create({
    baseURL : `${window.app_base_path}`,
	timeout : 1000 * 50
});

wrapperAxios.defaults.timeout = 1000 * 50;

//请求拦截
wrapperAxios.interceptors.request.use((config) => {

	let token = window.localStorage.token;
	if (token) {
	  config.headers.Authorization = `token ${token}`
	}
	return config
})

//响应拦截
wrapperAxios.interceptors.response.use(
	(resp) => {
		if (resp.status === 200) {
			return resp.data
		} else {
			// 网络错误
			return { 
				errorCode: -1, 
				isSuccess: false 
			}
		}
	},
	(err) => {
		logger.error(`wrapperAxios.interceptors.response -> ${JSON.stringify(err.response.data)}`);
	}
)

const CODE_STATUS = new Map([
	// 0x020407  //会议 ID 为空
	// 0x40071
	// 0x20207
	[0x04000a, {
		cn: '会议锁定,加入会议失败',
		en: 'The conference is locked. Failed to join the conference'
	}],
	[0x02040e, {
		cn: '网关不存在',
		en: 'Gateway does not exist'
	}],   // 132110
	[0x020004, {
		cn: '用户不存在',
		en: 'user does not exist'
	}],
	[0x020400, {
		cn: '会议不存在',
		en: 'The meeting does not exist'
	}],
	[0x020401, {
		cn: '会议已结束',
		en: 'Meeting ended'
	}],
	[0x020407, {
		cn: '会议 ID 为空',
		en: 'Meeting ID is empty'
	}],
	[0x20207, {
		cn: '用户类型不匹配',
		en: 'User type mismatch'
	}],
	[0x040072, {
		cn: '企业授权数已达最大,加入会议失败',
		en: 'The number of enterprise authorizations has reached the maximum. Failed to join the conference'
	}],
	[0x040009, {
		cn: '主持人未入会',
		en: 'The host is not a member'
	}],
	[0x020403, {
		cn: '参会密码不正确',
		en: 'Incorrect participation password'
	}],
	[0x020202, {
		cn: '账号或密码错误',
		en: 'Wrong account or password'
	}],
	[0x020201, {
		cn: '该账号已冻结',
		en: 'The account has been frozen'
	}],
	[0x020203, {
		cn: '该账号已冻结/终端未授权',
		en: 'The account has been frozen/the terminal is not authorized'
	}],
	[0x020205, {
		cn: '用户不存在或终端未注册',
		en: 'The user does not exist or the terminal is not registered'
	}],
	[0x1000, {
		cn: '相同账号重复入会',
		en: 'Repeated membership of the same account'
	}],  // webapp 与 webapp 互踢提示
	[0x1100, {
		cn: '调用会见平台API出错',
		en: 'Error calling the meeting platform API'
	}],
	[0x1200, {
		cn: '没有可用GW',
		en: 'No GW available'
	}],
	[0x40007, {
		cn: '密码错误,拒绝加入会议',
		en: 'Password error, refused to join the conference'
	}],
	[0x4000d, {
		cn: '超出会议授权数,加入会议失败',
		en: 'The number of conference authorizations exceeded. Failed to join the conference'
	}],
	[0x4000f, {
		cn: '没有可用的mp',
		en: 'No mp available'
	}],
	[0x04001b, {
		cn: '会议已经召开,请求入会时没有可用的netmp',
		en: 'The meeting has been held, and there is no netmp available at the time of requesting membership'
	}],
	[0x20003, {
		cn: '无效的令牌',
		en: 'Invalid token'
	}],
	[0x2030d, {
		cn: '配置尚未启动',
		en: 'Configuration has not been started'
	}],
	[0x020420, {
		cn: '已达到会议最大研讨会人数',
		en: 'The maximum number of seminars has been reached'
	}],
	[0x020421, {
		cn: '企业无研讨会权限',
		en: 'The enterprise has no workshop permission'
	}],
	[0x020422, {
		cn: '已达到企业最大研讨会人数',
		en: 'The maximum number of seminars has been reached'
	}],
	[0x020423, {
		cn: '企业研讨会授权未生效',
		en: 'The authorization of the enterprise seminar is not effective'
	}],
	[0x020424, {
		cn: '企业研讨会授权已到期',
		en: 'Enterprise seminar authorization has expired'
	}],
	[0x20425, {
		cn: '客户端不支持研讨会模式',
		en: 'The client does not support seminar mode'
	}],  //  132133
	[0xf0001, {
		cn: '主持人开启呼叫等待,加入会议失败',
		en: 'The host opened call waiting and failed to join the meeting'
	}],

	[458752, {
		cn: '由于语音识别服务已超过授权限额，实时字幕功能暂时无法使用，请联系系统管理员进行处理。',
		en: 'Due to the voice recognition service exceeding the authorized quota, the real-time subtitle function is temporarily unavailable. Please contact the system administrator for assistance.'
	}],
	[458753, {
		cn: '由于语音识别服务出现故障，实时字幕功能无法正常使用，请及时与系统管理员联系。',
		en: 'Due to a malfunction of the voice recognition service, the real-time subtitle function cannot be used normally. Please contact the system administrator in time.'
	}],

	[458754, {
		cn: '无法找到支持的服务，用户可以过一段时间重试',
		en: 'Unable to find a supported service, user can retry after a period of time'
	}],
	[458755, {
		cn: '认证失败，不是合法用户',
		en: 'Authentication failed, not a legal user'
	}],
	[458756, {
		cn: '未找到访问资源，例如URL错误',
		en: 'Access resource not found, such as URL error'
	}],
	[0x04008F, {
		cn: '由于语音识别服务出现故障，实时字幕功能无法正常使用，请及时与系统管理员联系。',
		en: 'Due to a malfunction of the voice recognition service, the real-time subtitle function cannot be used normally. Please contact the system administrator in time.'
	}],
	[0x040090, {
		cn: '由于语音识别服务出现故障，实时字幕功能无法正常使用，请及时与系统管理员联系。',
		en: 'Due to a malfunction of the voice recognition service, the real-time subtitle function cannot be used normally. Please contact the system administrator in time.'
	}],

	// 录制相关错误码
	[0x040076, {
		cn: '企业录制授权时间已经到期，开启失败',
		en: 'complicences rec timeout so start error'
	}],
	[0x040077, {
		cn: '获取企业录制授权失败，开启失败',
		en: 'get rec complicences error so start error'
	}],
	[0x040078, {
		cn: '录制授权已达最大，开启失败',
		en: 'rec exceed companymaxnum licence so start error'
	}],
	[0x04007A, {
		cn: '企业录制授权时间未开始，开启失败',
		en: 'complicences rec time not start so start error'
	}],
	[0x040020, {
		cn: '会议录制已停止（40020）',
		en: 'Meeting Recording Stopped（40020）'
	}],
	[0x040021, {
		cn: '开启录制失败（40021）',
		en: 'Opening recording failed（40021）'
	}],
	[0x040022, {
		cn: '开启录制失败（40022）',
		en: 'Opening recording failed（40022）'
	}],
	[0x040023, {
		cn: '服务繁忙，请稍后再试（40023）',
		en: 'Service busy，please try again later（40023）'
	}],
	[0x040024, {
		cn: '服务繁忙，请稍后再试（40024）',
		en: 'Service busy，please try again later（40024）'
	}],
	[0x040025, {
		cn: '服务繁忙，请稍后再试（40025）',
		en: 'Service busy， please try again later（40025）'
	}],
	[0x040026, {
		cn: '服务繁忙，请稍后再试（40026）',
		en: 'Service busy，please try again later（40026）'
	}],
	[0x040027, {
		cn: '会议录制已停止（40027）',
		en: 'Meeting Recording Stopped（40027）'
	}],
	[0x040028, {
		cn: '开启录制失败（40028）',
		en: 'Opening recording failed（40028）'
	}],
	[0x040029, {
		cn: '录制服务异常，请稍后再试（40029）',
		en: 'Recording service exception，please try again later（40029）'
	}],
	[0x04002A, {
		cn: '未开通录制服务',
		en: 'The recording service is not opened'
	}],
	[0x04002B, {
		cn: '录制授权已达上限',
		en: 'The recording license limit has been reached'
	}],
	[0x04002C, {
		cn: '开启录制失败（4002C）',
		en: 'Opening recording failed（4002C）'
	}],
	[0x04002D, {
		cn: '开启录制失败（4002D）',
		en: 'Opening recording failed（4002D）'
	}],
	[0x04002E, {
		cn: '开启录制失败（4002E）',
		en: 'Opening recording failed（4002E）'
	}],
	[0x04002F, {
		cn: '开启录制失败（4002F）',
		en: 'Opening recording failed（4002F）'
	}],

	[0x40092, {
		cn: '联席主持人数量已到上限',
		en: 'Co-host limit reached'
	}],
	[0x020702, {
		cn: '手机号不存在',
		en: 'The phone number does not exist'
	}],
	[0x020703, {
		cn: '发送过于频繁',
		en: 'Send frequently'
	}],
	[0x020704, {
		cn: '达到每天最大限度',
		en: 'Reach the maximum per day'
	}],
	[0x020705, {
		cn: '短信发送失败（20705）',
		en: 'SMS sending failed (20705)'
	}],
	[0x020706, {
		cn: '验证码为空',
		en: 'The verification code is empty'
	}],
	[0x020707, {
		cn: '验证码已过期',
		en: 'The verification code has expired'
	}],
	[0x020708, {
		cn: '请输入正确验证码',
		en: 'Please enter the correct verification code'
	}],

	[0x020139, {
		cn: '中邮第三方认证时，认证失败	第三方认证失败',
		en: 'Third-party authentication failed'
	}],
	[0x020140, {
		cn: '新账号请先在认证平台登录后，再登录本系统	新账号请先在认证平台登录后，再登录本系统',
		en: 'For new accounts，please log in to the authentication platform before logging in to the system'
	}],
	[0x020142, {
		cn: '手机号认证失败，请重试',
		en: 'Mobile phone number authentication failed，please try again'
	}],
	[0xfffffffd, {
		cn: 'Sfugw 注册失败',
		en: 'Sfugw is not registered'
	}],
	[0x0, { cn: '服务器错误', en: 'Server Error' }]
]);

// 返回错误码描述信息
export const getErrMesage = (code, lang, includesCode = true) => {

	logger.debug(`getErrMesage参数:code:${code} lang:${lang}`);

	if (CODE_STATUS.has(code)) {

		return `${CODE_STATUS.get(code)[lang]}` + (includesCode ? `(0x${code.toString(16)})` : '');
	} else {

		return  `${CODE_STATUS.get(0x0)[lang]}` + (includesCode ? `(0x${code.toString(16)})` : '');
	}
}

/**
 * Clones the given object/array.
 */
export const clone = (data) => {
	if (typeof data !== 'object')
		return {};

	return JSON.parse(JSON.stringify(data));
}

/**
 * 数组对象排序方法
 * @param {*} prop
 * @returns
 */
export const objSortCompare = (prop) => {

	const _propCompare = (x, y) => {

		const _value1 = x[prop];
		const _value2 = y[prop];

		if (_value1.includes('-') && _value2.includes('-')) {

			let _suffixValue1 = _value1.split('-')[1];
			let _suffixValue2 = _value2.split('-')[1];

			if (!isNaN(Number(_suffixValue1)) && !isNaN(Number(_suffixValue2))) {

				_suffixValue1 = Number(_suffixValue1);
				_suffixValue2 = Number(_suffixValue2);
			}

			if (_suffixValue1 < _suffixValue2) {

				return -1
			} else if (_suffixValue1 > _suffixValue2) {

				return 1
			} else {

				return 0
			}
		} else {

			return 0
		}
	}

	return _propCompare
}

export const sortCompare = (x, y) => {

	if (x.includes('-') && y.includes('-')) {
		let _val1 = x.split('-')[1];
		let _val2 = y.split('-')[1];

		if (!isNaN(Number(_val1)) && !isNaN(Number(_val2))) {
			_val1 = Number(_val1);
			_val2 = Number(_val2);
		}

		if (_val1 < _val2) {
			return -1
		} else if (_val1 > _val2) {
			return 1
		} else {
			return 0
		}
	} else {
		return 0
	}
}

/**
 * 字符串截取(中英文混排)
 * @param {*} str
 * @param {*} len
 * @param {*} hasDot
 * @returns
 */
export const subString = (str, len, hasDot) => {
	if (str) {

		let newLength = 0;
		let newStr = '';
		const chineseRegex = /[^\x00-\xff]/g;
		let singleChar = '';
		const strLength = str.replace(chineseRegex, '**').length;
		for (let i = 0; i < strLength; i++) {
			singleChar = str.charAt(i).toString();
			if (singleChar.match(chineseRegex) != null) {
				newLength += 2;
			}
			else {
				newLength++;
			}
			if (newLength > len) {
				break;
			}
			newStr += singleChar;
		}

		if (hasDot && strLength > len) {
			newStr += '...';
		}
		return newStr;
	} else {

		return '';
	}
}

/**
 * 字符串截取(中英文混排)信息(截取后的字符串与行数)
 *
 * @param {*} str
 * @param {*} maxLength 可显示窗口长度(可能多行)
 * @param {*} hasDot
 * @param {*} rowLength  每行长度(半角)
 * @returns
 */
export const subStringInfo = (str, maxLength, hasDot, rowLength) => {

	const _getSubString = (cutStr, startIdx) => {

		let  result = '',
			strlen = cutStr.length, // 字符串长度
			chrlen = cutStr.replace(/[^\x00-\xff]/g, '**').length; // 字节长度

		if (chrlen <= startIdx) { return cutStr; }

		for (let i = 0, j = 0; i < strlen; i++) {
			let chr = cutStr.charAt(i);
			if (/[\x00-\xff]/.test(chr)) {
				j++; // ascii码为0-255，一个字符就是一个字节的长度
			} else {
				j += 2; // ascii码为0-255以外，一个字符就是两个字节的长度
			}
			if (j <= startIdx) { // 当加上当前字符以后，如果总字节长度小于等于L，则将当前字符真实的+在result后
				result += chr;
			} else { // 反之则说明result已经是不拆分字符的情况下最接近L的值了，直接返回
				return result;
			}
		}
	};

	if (str) {

		let _newLength = 0;
		let _newStr = '';
		const _chineseRegex = /[^\x00-\xff]/g;
		let _singleChar = '';

		let _maskingStr = str.replace(_chineseRegex, '**');
		let  _origLength = _maskingStr.length;

		let _origLines = Number.isInteger(rowLength) ? Math.ceil(_origLength / rowLength) : 1;
		let _lines = _origLines;

		if (_origLines > 2) {

			// 需要将 str 中的内容前一部分截取掉
			const _hideStr = _getSubString(str, (_origLines - 2) * rowLength);
			str = str.replace(_hideStr, '');

			_origLength = str.replace(_chineseRegex, '**').length;
			_lines = Number.isInteger(rowLength) ? Math.ceil(_origLength / rowLength) : 1;
		}

		for (let i = 0; i < _origLength; i++) {

			_singleChar = str.charAt(i).toString();
			if (_singleChar.match(_chineseRegex) != null) {

				_newLength += 2;
			}
			else {

				_newLength++;
			}

			if (_newLength > maxLength) {
				break;
			}
			_newStr += _singleChar;
		}

		if (hasDot && _origLength > maxLength) {
			_newStr += '...';
		}

		const _length = _newStr.replace(_chineseRegex, '**').length;

		const _textInfo = {
			text       : _newStr,
			length     : _length,
			lines      : _origLines,
			origLength : _origLength,
			origLines  : _origLines
		};

		logger.debug(`subStringInfo : maxLength:${maxLength} rowLength: ${rowLength} -> ${JSON.stringify(_textInfo)}`);

		return _textInfo;
	} else {

		return '';
	}
}

/**
 * 字符串截取(中英文混排)信息(截取后的字符串与行数)返回内容是数组
 *
 * @param {*} origText
 * @param {*} displayLines 可显示窗口显示行数
 * @param {*} rowLength  每行长度(半角)
 * @returns
 */
export const subStringInfo2 = (origText, displayLines, rowLength) => {

    const _getSubString = (cutStr, startIdx) => {

        let result = '',
            strLen = cutStr.length, // 字符串长度
            chrLen = cutStr.replace(/[^\x00-\xff]/g, '**').length; // 字节长度

        if (chrLen <= startIdx) { return cutStr; }

        for (let i = 0, j = 0; i < strLen; i++) {
            let chr = cutStr.charAt(i);
            if (/[\x00-\xff]/.test(chr)) {
                j++; // ascii码为0-255，一个字符就是一个字节的长度
            } else {
                j += 2; // ascii码为0-255以外，一个字符就是两个字节的长度
            }
            if (j <= startIdx) { // 当加上当前字符以后，如果总字节长度小于等于L，则将当前字符真实的+在result后
                result += chr;
            } else { // 反之则说明result已经是不拆分字符的情况下最接近L的值了，直接返回
                return result;
            }
        }
    };

	if (origText) {
		const _chineseRegex = /[^\x00-\xff]/g;

		const _maskingText = origText.replace(_chineseRegex, '**');
		const _origLength = _maskingText.length;

		const _origLines = Number.isInteger(rowLength) ? Math.ceil(_origLength / rowLength) : 1;
		let _newLength = _origLength;

		let _origTexts = [];

		for (let i = 0; i < _origLines; i++) {

			let _text = _getSubString(origText, rowLength);
			_origTexts.push(_text);

			origText = origText.replace(_text, '');
		}

		let _texts = _origTexts;
		if (_origLines > displayLines) {

			_texts = _origTexts.slice(_origLines - displayLines);

			_newLength = _texts.join('').replace(_chineseRegex, '**').length;
		}

		const _textInfo = {
			texts      : _texts,
			length     : _newLength,
			lines      : _texts.length,
			origTexts  : _origTexts,
			origLength : _origLength,
			origLines  : _origLines
		};

		return _textInfo;
	} else {

		return '';
	}
}

/**
 * Cookies 设置
 * @param {*} name
 * @param {*} value
 */
export const setCookies = (name, value) => {

	const  days = 30;
    const  exp = new Date();

	exp.setTime(exp.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = name + "=" + escape(value) + ";expires=" + exp.toGMTString();
}

/**
 *  Cookies提取
 * @param {*} name
 * @returns
 */
export const getCookies = (name) => {
    let arr, reg = new RegExp("(^| )" + name + "=([^;]*)(;|$)");
    if (arr = document.cookie.match(reg))
        return unescape(arr[2]);
    else
        return null;
}